import jsPDF from 'jspdf';

/**
 * Generate PDF documentation with cover page (DIN A4)
 */
export const generateDocumentationPDF = (content, title = 'Besper Documentation') => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  // Helper to add new page if needed
  const checkNewPage = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper to add text with word wrap
  const addText = (text, fontSize, fontWeight = 'normal', color = '#1f2937', lineHeight = 1.5) => {
    if (!text) return yPosition;
    
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', fontWeight);
    pdf.setTextColor(color);
    
    const lines = pdf.splitTextToSize(String(text), contentWidth);
    lines.forEach((line) => {
      if (checkNewPage(fontSize * lineHeight)) {
        yPosition = margin;
      }
      pdf.text(line, margin, yPosition);
      yPosition += fontSize * lineHeight;
    });
    
    return yPosition;
  };

  // Cover Page
  pdf.setFillColor(2, 45, 84); // Primary color
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(48);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Besper', pageWidth / 2, 100, { align: 'center' });
  
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Documentation', pageWidth / 2, 120, { align: 'center' });
  
  pdf.setFontSize(14);
  pdf.text(title, pageWidth / 2, 140, { align: 'center' });
  
  // Date
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  pdf.setFontSize(12);
  pdf.text(`Generated on ${date}`, pageWidth / 2, pageHeight - 40, { align: 'center' });
  
  // New page for content
  pdf.addPage();
  yPosition = margin;

  // Table of Contents
  addText('Table of Contents', 20, 'bold', '#022d54');
  yPosition += 10;

  let tocItemNumber = 1;
  content.forEach(section => {
    addText(`${tocItemNumber}. ${section.title}`, 14, 'bold');
    tocItemNumber++;
    
    if (section.items) {
      section.items.forEach(item => {
        if (item.groupHeader) {
          addText(`   ${item.title}`, 12, 'normal', '#6b7280');
        } else {
          addText(`   ${item.title}`, 11, 'normal', '#9ca3af');
        }
      });
    }
  });

  // Content
  content.forEach(section => {
    checkNewPage(30);
    yPosition += 10;
    
    // Section Title
    addText(section.title, 24, 'bold', '#022d54');
    yPosition += 5;

    if (section.items) {
      section.items.forEach(item => {
        checkNewPage(20);
        yPosition += 10;

        if (item.groupHeader) {
          // Group Header
          addText(item.title, 16, 'bold', '#022d54');
          yPosition += 5;

          // Group Items
          if (item.items) {
            item.items.forEach(groupItem => {
              renderItem(pdf, groupItem, margin, yPosition, contentWidth, pageHeight, () => {
                yPosition = margin;
                pdf.addPage();
              });
              yPosition += 15;
            });
          }
        } else {
          // Regular Item
          renderItem(pdf, item, margin, yPosition, contentWidth, pageHeight, () => {
            yPosition = margin;
            pdf.addPage();
          });
          yPosition += 15;
        }
      });
    }
  });

  return pdf;
};

/**
 * Render a documentation item to PDF
 */
const renderItem = (pdf, item, margin, yPos, contentWidth, pageHeight, onNewPage) => {
  let yPosition = yPos;

  const checkNewPage = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      onNewPage();
      return margin;
    }
    return yPosition;
  };

  const addText = (text, fontSize, fontWeight = 'normal', color = '#1f2937', lineHeight = 1.5) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', fontWeight);
    pdf.setTextColor(color);
    
    const lines = pdf.splitTextToSize(text, contentWidth);
    lines.forEach((line) => {
      yPosition = checkNewPage(fontSize * lineHeight);
      pdf.text(line, margin, yPosition);
      yPosition += fontSize * lineHeight;
    });
    
    return yPosition;
  };

  // Item Title
  addText(item.title, 18, 'bold', '#022d54');
  yPosition += 5;

  // Description
  if (item.description) {
    addText(item.description, 12, 'normal', '#6b7280');
    yPosition += 5;
  }

  // Content
  if (item.content) {
    // Overview
    if (item.content.overview) {
      yPosition += 5;
      addText(item.content.overview, 11, 'normal');
      yPosition += 5;
    }

    // Steps
    if (item.content.steps) {
      item.content.steps.forEach((step, index) => {
        yPosition = checkNewPage(30);
        yPosition += 5;
        addText(`${step.title}`, 14, 'bold');
        yPosition += 3;
        if (step.description) {
          addText(step.description, 11, 'normal', '#6b7280');
          yPosition += 3;
        }
        if (step.code) {
          yPosition = checkNewPage(20);
          addCodeBlock(pdf, step.code, margin, yPosition, contentWidth, pageHeight, onNewPage);
          yPosition += 15;
        }
      });
    }

    // Methods
    if (item.content.methods) {
      item.content.methods.forEach((method, index) => {
        yPosition = checkNewPage(30);
        yPosition += 5;
        addText(`${method.title}`, 14, 'bold');
        yPosition += 3;
        if (method.description) {
          addText(method.description, 11, 'normal', '#6b7280');
          yPosition += 3;
        }
        if (method.code) {
          yPosition = checkNewPage(20);
          addCodeBlock(pdf, method.code, margin, yPosition, contentWidth, pageHeight, onNewPage);
          yPosition += 15;
        }
      });
    }
  }
};

/**
 * Add code block to PDF
 */
const addCodeBlock = (pdf, code, margin, yPos, contentWidth, pageHeight, onNewPage) => {
  let yPosition = yPos;
  const codeMargin = 5;
  const codeWidth = contentWidth - (codeMargin * 2);
  const fontSize = 9;
  const lineHeight = 4;

  // Background
  pdf.setFillColor(248, 250, 252);
  pdf.rect(margin + codeMargin, yPosition - fontSize, codeWidth, fontSize * 2, 'F');

  // Code text
  pdf.setFontSize(fontSize);
  pdf.setFont('courier', 'normal');
  pdf.setTextColor('#1f2937');
  
  const lines = pdf.splitTextToSize(code, codeWidth);
  lines.forEach((line) => {
    if (yPosition + lineHeight > pageHeight - margin) {
      onNewPage();
      yPosition = margin;
    }
    pdf.text(line, margin + codeMargin, yPosition);
    yPosition += lineHeight;
  });

  return yPosition;
};

/**
 * Generate TXT documentation
 */
export const generateDocumentationTXT = (content, title = 'Besper Documentation') => {
  let text = '';
  
  // Header
  text += '='.repeat(80) + '\n';
  text += 'BESPER DOCUMENTATION\n';
  text += title + '\n';
  text += 'Generated on ' + new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }) + '\n';
  text += '='.repeat(80) + '\n\n';

  // Table of Contents
  text += 'TABLE OF CONTENTS\n';
  text += '-'.repeat(80) + '\n';
  let tocNumber = 1;
  content.forEach(section => {
    text += `${tocNumber}. ${section.title}\n`;
    tocNumber++;
    if (section.items) {
      section.items.forEach(item => {
        if (item.groupHeader) {
          text += `   ${item.title}\n`;
        } else {
          text += `      - ${item.title}\n`;
        }
      });
    }
  });
  text += '\n' + '='.repeat(80) + '\n\n';

  // Content
  content.forEach(section => {
    text += '\n' + '='.repeat(80) + '\n';
    text += section.title.toUpperCase() + '\n';
    text += '='.repeat(80) + '\n\n';

    if (section.items) {
      section.items.forEach(item => {
        if (item.groupHeader) {
          text += '\n' + '-'.repeat(80) + '\n';
          text += item.title.toUpperCase() + '\n';
          text += '-'.repeat(80) + '\n\n';

          if (item.items) {
            item.items.forEach(groupItem => {
              text += renderItemTXT(groupItem);
            });
          }
        } else {
          text += renderItemTXT(item);
        }
      });
    }
  });

  return text;
};

/**
 * Render item to text
 */
const renderItemTXT = (item) => {
  let text = '';

  // Title
  text += item.title + '\n';
  text += '-'.repeat(item.title.length) + '\n\n';

  // Description
  if (item.description) {
    text += item.description + '\n\n';
  }

  // Content
  if (item.content) {
    // Overview
    if (item.content.overview) {
      text += item.content.overview + '\n\n';
    }

    // Steps
    if (item.content.steps) {
      item.content.steps.forEach((step, index) => {
        text += `${step.title}\n`;
        if (step.description) {
          text += step.description + '\n';
        }
        if (step.code) {
          text += '\n```\n';
          text += step.code + '\n';
          text += '```\n\n';
        }
      });
    }

    // Methods
    if (item.content.methods) {
      item.content.methods.forEach((method, index) => {
        text += `${method.title}\n`;
        if (method.description) {
          text += method.description + '\n';
        }
        if (method.code) {
          text += '\n```\n';
          text += method.code + '\n';
          text += '```\n\n';
        }
      });
    }

    // Setup
    if (item.content.setup) {
      item.content.setup.forEach((setup, index) => {
        text += `${setup.title}\n`;
        if (setup.description) {
          text += setup.description + '\n';
        }
        if (setup.code) {
          text += '\n```\n';
          text += setup.code + '\n';
          text += '```\n\n';
        }
      });
    }

    // Installation
    if (item.content.installation) {
      item.content.installation.forEach((inst, index) => {
        text += `${inst.title}\n`;
        if (inst.description) {
          text += inst.description + '\n';
        }
        if (inst.code) {
          text += '\n```\n';
          text += inst.code + '\n';
          text += '```\n\n';
        }
      });
    }

    // Usage
    if (item.content.usage) {
      item.content.usage.forEach((usage, index) => {
        text += `${usage.title}\n`;
        if (usage.description) {
          text += usage.description + '\n';
        }
        if (usage.code) {
          text += '\n```\n';
          text += usage.code + '\n';
          text += '```\n\n';
        }
      });
    }
  }

  text += '\n' + '='.repeat(80) + '\n\n';
  return text;
};

